import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { FileText, Upload, Download, Folder, File, Search, Filter, Archive, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoriaColors = {
  statuto: 'bg-indigo-100 text-indigo-800',
  regolamento: 'bg-green-100 text-green-800',
  atto_costitutivo: 'bg-amber-100 text-amber-800',
  delibere: 'bg-cyan-100 text-cyan-800',
  verbali_assemblea: 'bg-rose-100 text-rose-800',
  privacy: 'bg-purple-100 text-purple-800',
  liberatoria: 'bg-blue-100 text-blue-800',
  modulistica: 'bg-yellow-100 text-yellow-800',
  assicurativo: 'bg-red-100 text-red-800',
  report: 'bg-slate-100 text-slate-800',
  altro: 'bg-gray-100 text-gray-800'
};

const categorie = [
  { value: 'statuto', label: 'Statuto' },
  { value: 'regolamento', label: 'Regolamento' },
  { value: 'atto_costitutivo', label: 'Atto Costitutivo' },
  { value: 'delibere', label: 'Delibere' },
  { value: 'verbali_assemblea', label: 'Verbali di Assemblea' },
  { value: 'privacy', label: 'Privacy' },
  { value: 'liberatoria', label: 'Liberatoria' },
  { value: 'modulistica', label: 'Modulistica' },
  { value: 'assicurativo', label: 'Assicurativo' },
  { value: 'report', label: 'Report' },
  { value: 'altro', label: 'Altro' }
];

export default function Documenti() {
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('all');
  const [selectedCategoria, setSelectedCategoria] = useState('modulistica');
  const fileInputRef = useRef(null);
  
  // Backup states
  const [generatingBackup, setGeneratingBackup] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [showBackupHistory, setShowBackupHistory] = useState(false);

  useEffect(() => {
    fetchDocumenti();
  }, [categoriaFilter]);

  const fetchDocumenti = async () => {
    try {
      let url = `${API_URL}/api/documenti`;
      if (categoriaFilter && categoriaFilter !== 'all') {
        url += `?categoria=${categoriaFilter}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setDocumenti(await response.json());
      }
    } catch (error) {
      console.error('Error fetching documenti:', error);
      toast.error('Errore nel caricamento dei documenti');
    } finally {
      setLoading(false);
    }
  };

  // Backup functions
  const handleBackup = async () => {
    setGeneratingBackup(true);
    try {
      const response = await axios.post(`${API_URL}/api/backup/genera`, {}, { withCredentials: true });
      
      if (response.data.success) {
        // Decode base64 and download
        const byteCharacters = atob(response.data.zip_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/zip' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`Backup scaricato: ${response.data.stats.totale_file} file`);
      }
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Errore durante la generazione del backup');
    } finally {
      setGeneratingBackup(false);
    }
  };

  const fetchBackupHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/backup/storico`, { withCredentials: true });
      setBackupHistory(response.data.backups || []);
      setShowBackupHistory(true);
    } catch (error) {
      console.error('Error fetching backup history:', error);
      toast.error('Errore nel caricamento dello storico');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validazione lato client
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.docx'];
    const maxSizeMB = 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    const fileName = file.name.toLowerCase();
    const extension = fileName.includes('.') ? '.' + fileName.split('.').pop() : '';
    
    if (!allowedExtensions.includes(extension)) {
      toast.error(`Tipo di file non consentito. Formati accettati: PDF, JPG, PNG, DOCX`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    if (file.size > maxSizeBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`File troppo grande (${sizeMB}MB). Limite massimo: ${maxSizeMB}MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = new URL(`${API_URL}/api/documenti`);
      url.searchParams.append('categoria', selectedCategoria);

      console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);

      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData
      });

      console.log('Upload response status:', response.status);

      if (response.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        window.location.href = '/login';
        return;
      }

      if (response.ok) {
        try {
          const data = await response.json();
          toast.success(`Documento "${file.name}" caricato con successo`);
        } catch {
          toast.success(`Documento "${file.name}" caricato`);
        }
        fetchDocumenti();
      } else {
        // Clone response before reading to avoid "body already consumed" errors
        let errorMessage = `Errore durante il caricamento (${response.status})`;
        try {
          const clonedResponse = response.clone();
          const responseText = await clonedResponse.text();
          console.error('Server error response:', response.status, responseText);
          try {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.detail || errorData.reason || errorData.message || errorMessage;
          } catch {
            if (responseText && responseText.length < 500) errorMessage = responseText;
          }
        } catch (parseError) {
          console.error('Error reading server response:', parseError);
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Errore di connessione: ${error.message || 'Verifica la tua connessione internet'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (documento) => {
    try {
      const response = await fetch(`${API_URL}/api/documenti/${documento.documento_id}/download`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documento.nome_file;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Download completato');
      } else {
        toast.error('Errore durante il download');
      }
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const filteredDocumenti = documenti.filter(d => {
    if (!search) return true;
    return d.nome_file?.toLowerCase().includes(search.toLowerCase());
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documenti-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Documenti</h1>
          <p className="page-subtitle">Archivio documentale dell'associazione</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCategoria} onValueChange={setSelectedCategoria}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categorie.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          />
          <Button 
            className="btn-primary" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-btn"
          >
            {uploading ? (
              <span className="spinner mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Carica Documento
          </Button>
          <Button 
            variant="outline"
            onClick={handleBackup}
            disabled={generatingBackup}
            data-testid="backup-btn"
            className="border-green-500 text-green-700 hover:bg-green-50"
          >
            {generatingBackup ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Archive className="w-4 h-4 mr-2" />
            )}
            {generatingBackup ? 'Generazione...' : 'Backup'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome file..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-categoria">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tutte le categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categorie.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Folder className="w-5 h-5 text-[#0055BB]" />
            Archivio Documenti
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredDocumenti.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" />
              <p className="empty-state-title">Nessun documento trovato</p>
              <p className="empty-state-text">
                {search || categoriaFilter !== 'all' 
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'Carica il primo documento per iniziare'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome File</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="hidden md:table-cell">Data Caricamento</TableHead>
                    <TableHead className="hidden lg:table-cell">Versione</TableHead>
                    <TableHead className="w-24">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocumenti.map((documento) => (
                    <TableRow key={documento.documento_id} data-testid={`documento-row-${documento.documento_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-slate-400" />
                          <span className="font-medium truncate max-w-[200px]">
                            {documento.nome_file}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={categoriaColors[documento.categoria] || 'bg-slate-100 text-slate-800'}>
                          {documento.categoria?.charAt(0).toUpperCase() + documento.categoria?.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {documento.created_at ? new Date(documento.created_at).toLocaleDateString('it-IT') : '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        v{documento.versione || 1}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(documento)}
                          data-testid={`download-${documento.documento_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {filteredDocumenti.length > 0 && (
        <div className="text-sm text-slate-500 text-center">
          {filteredDocumenti.length} documenti archiviati
        </div>
      )}
    </div>
  );
}
